import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { MutationError, ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon, type IconName } from "../../ui/icons";
import { ReassignDialog } from "../prepare-offers/reassign-dialog";
import { useQueue, useQueueTransition } from "./use-queue";
import {
  bandLabelKey,
  isWebLeadItem,
  itemTypeLabelKey,
  leadSourceLabelKey,
  queueStatusLabelKey,
  PRIORITY_BANDS,
  QUEUE_ITEM_TYPES,
  webLeadId,
  type PriorityBand,
  type QueueAction,
  type QueueBundle,
  type QueueItem,
  type QueueItemType,
  type QueueFilter,
} from "./types";

// The priority band shown as a calm k-status pill — HIGH reads as the amber
// "expiring" cue, MEDIUM as review-blue, LOW as neutral. Never red.
const BAND_STATUS: Record<PriorityBand, string> = {
  HIGH: "k-status--expiring",
  MEDIUM: "k-status--review",
  LOW: "k-status--pending",
};

// The priority band as a hairline on the card's LEFT edge — the governance cue
// kept quiet: warm amber for HIGH, softer amber for MEDIUM, a neutral line for LOW.
const BAND_BORDER: Record<PriorityBand, string> = {
  HIGH: "var(--k-amber)",
  MEDIUM: "var(--k-amber-border)",
  LOW: "var(--k-line)",
};

// A soft icon tile per touch type — the glanceable left-hand glyph.
const TYPE_ICON: Record<QueueItemType, IconName> = {
  RENEWAL: "calendar",
  OPPORTUNITY: "suggestion",
  ALERT: "alert-triangle",
  SERVICE_FOLLOWUP: "tasks",
  NEW_LEAD: "household",
};

const fmt = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "—";

// AC2 — the daily queue. The advisor's morning surface: prioritized, bundled into
// household-touches (the no-spam discipline made visible), every action an
// explicit click (suggest-not-decide). Reads/actions ride the governance client.
export function QueuePage() {
  const { t } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me } = usePrincipal();
  // Supervisors/admins can devret (reassign) a lead item to another advisor. RBAC
  // is UX only — the API re-checks lead:assign on POST /v1/leads/:id/assign.
  const canReassign = hasGrant(me, "lead:assign", "own");
  const [filter, setFilter] = useState<QueueFilter>({});
  const [reassignLeadId, setReassignLeadId] = useState<string | null>(null);
  const { data, isLoading, error } = useQueue(filter);
  const transition = useQueueTransition();
  /* Set when the browser gives us no way to ask for the required text. See `act`. */
  const [promptUnavailable, setPromptUnavailable] = useState(false);

  // "3 Teklif Hazırla" — open the offer-authoring surface for a web lead, carrying
  // the lead id in the path and the glance context (name/plate) as route state so
  // the form can show who it's for without a second read.
  const prepareOffers = (item: QueueItem) => {
    const leadId = webLeadId(item);
    if (!leadId) return;
    navigate(`/leads/${leadId}/prepare-offers`, {
      state: { partyName: item.partyName, plate: item.plate },
    });
  };

  const act = (item: QueueItem, action: QueueAction) => {
    setPromptUnavailable(false);
    /* `globalThis.prompt?.()` yields undefined in TWO different situations that
       used to be treated the same: the user cancelled (fine — do nothing), and
       the environment has no prompt at all (not fine — the button is then dead
       and says nothing). Distinguishing them is the whole fix: `prompt` missing
       is reported; a cancelled prompt stays silent, because silence is the
       correct response to "I changed my mind". */
    const ask = (message: string): { ok: boolean; value?: string } => {
      if (typeof globalThis.prompt !== "function") {
        setPromptUnavailable(true);
        return { ok: false };
      }
      const value = globalThis.prompt(message)?.trim();
      return value ? { ok: true, value } : { ok: false };
    };

    if (action === "COMPLETE") {
      const r = ask(t("queue.disposition_prompt"));
      if (!r.ok) return;
      transition.mutate({ id: item.id, input: { action, disposition: r.value! } });
    } else if (action === "DISMISS") {
      const r = ask(t("queue.reason_prompt"));
      if (!r.ok) return;
      transition.mutate({ id: item.id, input: { action, reason: r.value! } });
    } else {
      transition.mutate({ id: item.id, input: { action } });
    }
  };

  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("queue.heading")}</h1>
          <p className="surface__subtitle">
            {t("queue.touch")} · {t("queue.filter.allBands")}
          </p>
        </div>
        {/* Üstlen / Tamamla / Vazgeç all fired and reported nothing, so a 409 on a
            stale item (already claimed elsewhere, already completed) left the row
            unchanged and the advisor clicking again. */}
        <MutationError
          error={transition.error}
          fallback={t("q.actionFailed")}
          offline={t("common.offline")}
        />
        {promptUnavailable && (
          <p role="alert" className="k-field__error" data-testid="queue-prompt-unavailable">
            {t("queue.promptUnavailable")}
          </p>
        )}
        <div className="surface__actions queue-filters">
          <div className="k-select">
            <select
              className="k-select__native"
              aria-label={t("queue.filter.allBands")}
              value={filter.band ?? ""}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  band: (e.target.value || undefined) as PriorityBand | undefined,
                }))
              }
            >
              <option value="">{t("queue.filter.allBands")}</option>
              {PRIORITY_BANDS.map((b) => (
                <option key={b} value={b}>
                  {t(bandLabelKey(b))}
                </option>
              ))}
            </select>
            <span className="k-select__chevron">
              <Icon name="chevron-down" size={16} />
            </span>
          </div>
          <div className="k-select">
            <select
              className="k-select__native"
              aria-label={t("queue.filter.allTypes")}
              value={filter.type ?? ""}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  type: (e.target.value || undefined) as QueueFilter["type"],
                }))
              }
            >
              <option value="">{t("queue.filter.allTypes")}</option>
              {QUEUE_ITEM_TYPES.map((ty) => (
                <option key={ty} value={ty}>
                  {t(itemTypeLabelKey(ty))}
                </option>
              ))}
            </select>
            <span className="k-select__chevron">
              <Icon name="chevron-down" size={16} />
            </span>
          </div>
        </div>
      </header>

      {isLoading && (
        <div className="k-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("queue.loading")}</span>
          </div>
        </div>
      )}
      {error && (
        <div className="k-note k-note--error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <p role="alert" className="k-note__desc">
              {error instanceof ProblemError ? error.problem.detail : t("queue.error")}
            </p>
          </div>
        </div>
      )}
      {!isLoading && !error && (data?.length ?? 0) === 0 && (
        <div className="k-empty">
          <div className="k-empty__icon">
            <Icon name="check-circle" size={26} />
          </div>
          <p className="k-empty__title">{t("queue.empty")}</p>
        </div>
      )}

      <ul className="queue-list">
        {(data ?? []).map((bundle) => (
          <BundleCard
            key={bundle.bundleKey}
            bundle={bundle}
            onAct={act}
            onPrepareOffers={prepareOffers}
            canReassign={canReassign}
            onReassign={setReassignLeadId}
            busy={transition.isPending}
          />
        ))}
      </ul>

      {reassignLeadId && (
        // The governed "Devret / Danışmana ata" dialog reused from prepare-offers
        // (#177): lists GET /v1/leads/assignable-advisors, POSTs the assignment.
        // On success we refetch the queue so the new owner is authoritative.
        <ReassignDialog
          leadId={reassignLeadId}
          onClose={() => setReassignLeadId(null)}
          onReassigned={() => {
            void qc.invalidateQueries({ queryKey: ["queue"] });
          }}
        />
      )}
    </section>
  );
}

function BundleCard({
  bundle,
  onAct,
  onPrepareOffers,
  canReassign,
  onReassign,
  busy,
}: {
  bundle: QueueBundle;
  onAct: (item: QueueItem, action: QueueAction) => void;
  onPrepareOffers: (item: QueueItem) => void;
  canReassign: boolean;
  onReassign: (leadId: string) => void;
  busy: boolean;
}) {
  const { t } = useT();
  // The band that colors the card's left edge — the touch's top item (the API
  // ranks the bundle by it), falling back to a neutral line.
  const leadBand = bundle.items[0]?.priorityBand ?? "LOW";
  return (
    <li
      className="k-card k-card--pad-sm"
      data-testid="bundle"
      data-household={bundle.householdId}
      style={{ borderLeft: `3px solid ${BAND_BORDER[leadBand]}` }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <span className="k-tablecard__title">{bundle.householdId}</span>
        <span style={{ fontSize: 12.5, color: "var(--k-muted)" }}>
          {bundle.items.length} {t("queue.itemsInBundle")} · 1 {t("queue.touch")}
        </span>
      </header>
      <ul className="bundle-items">
        {bundle.items.map((item) => {
          // GAP-AUDIT-3 D7 — a settled item has nothing left to act on. The
          // action row was unguarded on status, so DONE / DISMISSED / SUPPRESSED
          // rows still offered live Tamamla / Vazgeç buttons whose only outcome
          // was a server-side rejection. Claim was already status-guarded; the
          // rest now agree with it.
          const terminal =
            item.status === "DONE" || item.status === "DISMISSED" || item.status === "SUPPRESSED";
          return (
            <li
              key={item.id}
              className="queue-item"
              data-testid="queue-item"
              data-source={isWebLeadItem(item) ? (item.source ?? "web") : undefined}
            >
              <span className="k-stat__icon k-stat__icon--blue">
                <Icon name={TYPE_ICON[item.itemType]} size={18} />
              </span>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 }}
              >
                {isWebLeadItem(item) ? (
                  // An inbound-intake lead, now a first-class queue item: the calm
                  // source-accurate badge (Web premier / Telefon neutral) + the enriched
                  // partyName + intake-captured plate. Same band/SLA and actions as any
                  // other line — it sits in the queue by priority.
                  <span className="queue-suggestion queue-lead">
                    <span
                      className={`k-badge ${
                        item.source === "phone" ? "k-badge--standard" : "k-badge--premier"
                      }`}
                      data-testid="lead-web-badge"
                    >
                      {t(leadSourceLabelKey(item))}
                    </span>
                    {item.partyName && (
                      <span
                        className="lead-party"
                        style={{ fontWeight: 600, color: "var(--k-ink)" }}
                      >
                        {item.partyName}
                      </span>
                    )}
                  </span>
                ) : (
                  <span
                    className="queue-suggestion"
                    style={{ fontWeight: 600, color: "var(--k-ink)" }}
                  >
                    {t(itemTypeLabelKey(item.itemType))}
                  </span>
                )}
                <span className="queue-due" style={{ fontSize: 12.5, color: "var(--k-muted)" }}>
                  {t("queue.due")}: {fmt(item.dueAt)}
                  {item.slaDueAt ? ` · ${t("queue.sla")}: ${fmt(item.slaDueAt)}` : ""}
                  {isWebLeadItem(item) && item.plate ? (
                    <>
                      {" · "}
                      <span data-testid="lead-plate">
                        {t("leads.plate")}: {item.plate}
                      </span>
                    </>
                  ) : null}
                </span>
              </div>
              <span className={`k-status ${BAND_STATUS[item.priorityBand]}`}>
                <span className="k-status__dot" />
                {t(bandLabelKey(item.priorityBand))}
              </span>
              <span className="queue-actions">
                {terminal && (
                  <span
                    style={{ fontSize: 12.5, color: "var(--k-muted)" }}
                    data-testid="queue-settled"
                  >
                    {t(queueStatusLabelKey(item.status))}
                  </span>
                )}
                {!terminal && isWebLeadItem(item) && webLeadId(item) && (
                  // The web-lead's primary next step: author 1–3 offers → scored
                  // recommendation → the existing choose→tier2→present flow.
                  <button
                    type="button"
                    className="k-btn k-btn--primary k-btn--sm"
                    data-testid="prepare-offers"
                    disabled={busy}
                    onClick={() => onPrepareOffers(item)}
                  >
                    {t("queue.prepareOffers")}
                  </button>
                )}
                {item.status === "PENDING" && (
                  <button
                    type="button"
                    className="k-btn k-btn--soft k-btn--sm"
                    disabled={busy}
                    onClick={() => onAct(item, "CLAIM")}
                  >
                    {t("queue.claim")}
                  </button>
                )}
                {item.status === "IN_PROGRESS" && (
                  <span style={{ fontSize: 12.5, color: "var(--k-muted)" }}>
                    {t("queue.inProgress")}
                  </span>
                )}
                {!terminal && canReassign && isWebLeadItem(item) && webLeadId(item) && (
                  // Devret — reassign this lead's ownership to another advisor
                  // (supervisor/admin, lead:assign). Sits with the row's actions,
                  // beside the owner-driven claim/complete controls.
                  <button
                    type="button"
                    className="k-btn k-btn--soft k-btn--sm"
                    data-testid="queue-reassign"
                    disabled={busy}
                    onClick={() => onReassign(webLeadId(item)!)}
                  >
                    {t("po.reassign.open")}
                  </button>
                )}
                {!terminal && (
                  <>
                    <button
                      type="button"
                      className="k-btn k-btn--approve k-btn--sm"
                      data-testid="queue-complete"
                      disabled={busy}
                      onClick={() => onAct(item, "COMPLETE")}
                    >
                      {t("queue.complete")}
                    </button>
                    <button
                      type="button"
                      className="k-btn k-btn--ghost k-btn--sm"
                      data-testid="queue-dismiss"
                      disabled={busy}
                      onClick={() => onAct(item, "DISMISS")}
                    >
                      {t("queue.dismiss")}
                    </button>
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </li>
  );
}
